/* HeyGen Studio · admin marketing tool
   ───────────────────────────────────────────────────────────────────
   Iter 246 · Kicks off a HeyGen v2 avatar-video render for the Verqo
   landing-page hero and polls until the MP4 is ready.

   The script + scene list defaults come pre-loaded from the current
   founder-pitch spec so an admin can click "Generate" in ~30 seconds
   without hunting for URLs. Avatar & voice pickers hit our /api/heygen
   proxy (never expose the key to the browser).
*/
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Video, Sparkles, Trash2, Plus, Download, RefreshCw } from "lucide-react";

// ── Landing-page video defaults ──────────────────────────────────
// Full 115s script (see /app/marketing-assets/heygen-b-roll/SCRIPT.md).
const DEFAULT_SCRIPT = `Hi — I'm Zev, founder of Verqo. I spent years running wholesale distribution. And one thing became very clear.

Wholesale is fragmented. A supplier uses one platform to catalog products, another to take orders, another for payments, another for shipping, another for trade insurance, another for financing, another for inventory, another for accounting. None of them talk to each other. Teams waste hours every day switching tabs and reconciling by hand.

And then the worst part. Sellers wait weeks — sometimes months — to actually get paid. Every day starts with the same thing: chasing customers on email, picking up the phone, sending reminders, calling out overdue invoices. Money is stuck on paper. Cash flow gets crushed.

It gets even worse. You have no idea if the buyer you're about to ship to just defaulted on three other suppliers. You have no clean way to decide what terms to offer them. You extend Net-30 on a hunch — and hope.

That's why we built Verqo. One operating system where buyers discover suppliers, place orders, and get paid — all in one place. Sellers see a live trade score for every buyer, every day — refreshed from real payment behavior across the entire Verqo network. And when it's time to pay, buyers click one button. Sellers get paid instantly. No chasing. No reminders. No waiting.

And then the parts every wholesaler has been begging for. Both sides get invoice financing at competitive rates with one click. Every seller can insure any invoice with one click. And every parcel or pallet ships with one click — insured, tracked, and reconciled back to the order automatically.

Verqo wasn't built from theory. It was built from years of living this pain — millions of dollars moved, thousands of SKUs, hundreds of supplier relationships. Every inefficiency, we felt firsthand.

Our mission is simple. One connected wholesale operating system. Buy, sell, get paid, ship — instant. Come see it at verqo.com.`;

const PREVIEW_BASE = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SCENES = [
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/07-landing-hero.jpeg`, duration_ms: 10000, label: "Hook · Zev intro" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/08-pitch-cover.jpeg`,  duration_ms: 15000, label: "Fragmentation" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/09-buyer-invoices.jpeg`, duration_ms: 15000, label: "Payment chase" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/02-buyers-scored.jpeg`, duration_ms: 15000, label: "Seller blind spot" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/01-seller-dashboard.jpeg`, duration_ms: 20000, label: "What Verqo does" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/04-payouts.jpeg`,     duration_ms: 10000, label: "1-click financing" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/10-seller-insurance.jpeg`, duration_ms: 8000,  label: "1-click insurance" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/11-seller-shipping.jpeg`, duration_ms: 8000, label: "1-click shipping" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/06-buyer-score.jpeg`, duration_ms: 8000,  label: "Founder-market fit" },
  { image_url: `${PREVIEW_BASE}/marketing-assets/heygen-b-roll/07-landing-hero.jpeg`, duration_ms: 6000,  label: "Close · verqo.com" },
];

// Sensible defaults so a fresh admin can click Generate immediately.
const DEFAULT_AVATAR_ID = "Brandon_Business_Standing_Front_public";
const DEFAULT_VOICE_ID  = "Xai5EbCHraGPCrMzEqYp"; // Visionary Strategist · male · EN

export default function HeyGenStudio() {
  const [avatars, setAvatars]   = useState([]);
  const [voices, setVoices]     = useState([]);
  const [avatarId, setAvatarId] = useState(DEFAULT_AVATAR_ID);
  const [voiceId, setVoiceId]   = useState(DEFAULT_VOICE_ID);
  const [script, setScript]     = useState(DEFAULT_SCRIPT);
  const [scenes, setScenes]     = useState(DEFAULT_SCENES);
  const [busy, setBusy]         = useState(false);
  const [job, setJob]           = useState(null);
  const [err, setErr]           = useState("");
  const [history, setHistory]   = useState([]);
  const [avFilter, setAvFilter] = useState("business");

  // Load catalogs on mount.
  useEffect(() => {
    api.get("/heygen/avatars")
      .then(r => setAvatars((r.data?.avatars || []).filter(a => a?.avatar_id && a?.avatar_name)))
      .catch(() => {});
    api.get("/heygen/voices")
      .then(r => setVoices((r.data?.voices || []).filter(v => (v?.language || "").toLowerCase().startsWith("english"))))
      .catch(() => {});
    api.get("/heygen/videos?limit=10")
      .then(r => setHistory(r.data?.jobs || []))
      .catch(() => {});
  }, []);

  // Poll running job until it terminates.
  useEffect(() => {
    if (!job?.video_id) return;
    if (["completed", "failed"].includes(job.status)) return;
    const tick = async () => {
      try {
        const r = await api.get(`/heygen/videos/${job.video_id}`);
        setJob(r.data);
      } catch (e) {
        setErr("Polling failed — will retry");
      }
    };
    const id = setInterval(tick, 15000);
    tick(); // fire once immediately
    return () => clearInterval(id);
  }, [job?.video_id, job?.status]);

  const filteredAvatars = useMemo(() => {
    if (!avFilter) return avatars.slice(0, 60);
    const q = avFilter.toLowerCase();
    return avatars.filter(a => (a.avatar_name || "").toLowerCase().includes(q)).slice(0, 60);
  }, [avatars, avFilter]);

  const totalScriptChars  = script.length;
  const totalSceneSeconds = scenes.reduce((a, s) => a + (s.duration_ms || 0), 0) / 1000;

  const updateScene = (i, patch) =>
    setScenes(prev => prev.map((s, n) => (n === i ? { ...s, ...patch } : s)));
  const removeScene = i => setScenes(prev => prev.filter((_, n) => n !== i));
  const addScene    = () =>
    setScenes(prev => [...prev, { image_url: "", duration_ms: 10000, label: `Scene ${prev.length + 1}` }]);

  const generate = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/heygen/videos/generate", {
        script,
        avatar_id: avatarId,
        voice_id: voiceId,
        scenes: scenes
          .filter(s => s.image_url?.trim())
          .map(s => ({ image_url: s.image_url, duration_ms: s.duration_ms })),
        title: "Verqo · Founder pitch · Landing hero",
        caption: true,
      });
      setJob(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10 space-y-8" data-testid="heygen-studio">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-foreground/40">Admin · marketing</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Video className="w-7 h-7 text-lime-500" /> HeyGen Studio
          </h1>
          <p className="mt-1 text-sm text-foreground/60">
            Render the Verqo founder-pitch talking-head video — script + scenes below are the current landing-page spec.
          </p>
        </div>
        {history.length > 0 && (
          <Button variant="outline" size="sm" data-testid="refresh-history"
                  onClick={() => api.get("/heygen/videos?limit=10").then(r => setHistory(r.data?.jobs || []))}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh history
          </Button>
        )}
      </header>

      {/* ── Presenter ─────────────────────────────────────────────── */}
      <Card data-testid="presenter-card">
        <CardHeader><CardTitle className="text-base">1 · Presenter</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-foreground/50">Avatar</label>
              <Input value={avFilter} onChange={e => setAvFilter(e.target.value)} placeholder="Filter · e.g. business, brandon, zev" className="mt-1" data-testid="avatar-filter"/>
              <select
                value={avatarId}
                onChange={e => setAvatarId(e.target.value)}
                data-testid="avatar-select"
                className="mt-2 w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
              >
                {filteredAvatars.map(a => (
                  <option key={a.avatar_id} value={a.avatar_id}>{a.avatar_name}</option>
                ))}
              </select>
              <div className="mt-1 text-[10.5px] text-foreground/45 mono">{avatarId}</div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-foreground/50">Voice · English</label>
              <select
                value={voiceId}
                onChange={e => setVoiceId(e.target.value)}
                data-testid="voice-select"
                className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
              >
                {voices.slice(0, 200).map(v => (
                  <option key={v.voice_id} value={v.voice_id}>
                    {v.name} · {v.gender || "?"} · {v.language}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-[10.5px] text-foreground/45 mono">{voiceId}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Script ────────────────────────────────────────────────── */}
      <Card data-testid="script-card">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">2 · Script</CardTitle>
          <div className="text-[11px] text-foreground/50 mono tabular-nums">
            {totalScriptChars} / 5000 chars
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            value={script}
            onChange={e => setScript(e.target.value)}
            data-testid="script-textarea"
            className="min-h-[300px] font-mono text-[13px] leading-relaxed"
          />
        </CardContent>
      </Card>

      {/* ── Scenes ────────────────────────────────────────────────── */}
      <Card data-testid="scenes-card">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">3 · Scene track</CardTitle>
          <div className="text-[11px] text-foreground/50 mono tabular-nums">
            {scenes.length} scenes · ~{totalSceneSeconds.toFixed(0)}s target
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {scenes.map((s, i) => (
            <div key={i} className="grid grid-cols-[100px_1fr_120px_36px] gap-2 items-center" data-testid={`scene-row-${i}`}>
              <div className="text-[11px] text-foreground/55 mono uppercase tracking-[0.14em] truncate">{s.label || `Scene ${i+1}`}</div>
              <Input value={s.image_url} onChange={e => updateScene(i, { image_url: e.target.value })} placeholder="Public image URL" className="text-[12.5px]" data-testid={`scene-url-${i}`}/>
              <Input type="number" value={s.duration_ms} onChange={e => updateScene(i, { duration_ms: +e.target.value })} className="text-[12.5px]" data-testid={`scene-duration-${i}`}/>
              <Button variant="ghost" size="icon" onClick={() => removeScene(i)} data-testid={`scene-remove-${i}`}>
                <Trash2 className="w-4 h-4 text-foreground/50"/>
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addScene} className="mt-2" data-testid="add-scene">
            <Plus className="w-3.5 h-3.5 mr-1.5"/> Add scene
          </Button>
        </CardContent>
      </Card>

      {/* ── Generate ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <Button size="lg" disabled={busy || !script || !avatarId || !voiceId} onClick={generate} data-testid="generate-btn">
          <Sparkles className="w-4 h-4 mr-2"/> {busy ? "Submitting to HeyGen…" : "Generate 1080p MP4"}
        </Button>
        {err && <div className="text-sm text-red-600 mono" data-testid="error">{String(err)}</div>}
      </div>

      {/* ── Job status + player ───────────────────────────────────── */}
      {job && (
        <Card data-testid="job-card">
          <CardHeader><CardTitle className="text-base">Render job</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 text-[13px]">
              <div className="mono">{job.video_id}</div>
              <span className={`px-2 py-0.5 rounded-full text-[10.5px] mono uppercase tracking-[0.14em] ${
                job.status === "completed" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                job.status === "failed"    ? "bg-red-50 text-red-700 border border-red-200" :
                                             "bg-amber-50 text-amber-700 border border-amber-200"
              }`}>{job.status}</span>
              {job.duration && <span className="text-foreground/55">{job.duration}s</span>}
            </div>
            {job.mp4_url && (
              <>
                <video controls playsInline className="w-full rounded-lg border border-border" src={job.mp4_url} data-testid="preview-video"/>
                <a href={job.mp4_url} download="verqo-founder-pitch.mp4"
                   className="inline-flex items-center gap-1.5 text-sm text-lime-600 hover:underline" data-testid="download-mp4">
                  <Download className="w-3.5 h-3.5"/> Download MP4
                </a>
              </>
            )}
            {job.status === "processing" && (
              <div className="text-sm text-foreground/55">HeyGen renders in ~5–15 min. This page polls every 15 s.</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── History ───────────────────────────────────────────────── */}
      {history.length > 0 && (
        <Card data-testid="history-card">
          <CardHeader><CardTitle className="text-base">Recent renders</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {history.map(h => (
              <div key={h.video_id} className="grid grid-cols-[1fr_100px_120px_100px] gap-2 text-[12.5px] py-1 border-b border-border last:border-b-0">
                <div className="mono truncate">{h.title || h.video_id}</div>
                <div className="mono text-foreground/55">{h.status}</div>
                <div className="mono text-foreground/45">{h.created_at?.slice(0, 10)}</div>
                {h.mp4_url ? (
                  <a href={h.mp4_url} target="_blank" rel="noreferrer" className="text-lime-600 hover:underline">Open</a>
                ) : <div className="text-foreground/30">—</div>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
